/**
 * Shared subscription charge totals / display rows for Summary, Receipt,
 * Account Details, and (mirrored in API) confirmation emails.
 */

function pickNumber(...vals) {
  for (const raw of vals) {
    const v = Number(raw);
    if (Number.isFinite(v)) return v;
  }
  return null;
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Account / API subscription rows carry live `totalPrice` (admin-approved).
 * Create previews and invoice drafts usually do not.
 */
function isPersistedSubscriptionSource(source = {}) {
  return Boolean(
    source.id != null
    || source.subscriptionNumber
    || source.orderStatus
    || source.webPlanSnapshot
  );
}

/**
 * Normalize charges from an account subscription row, create payload, or receipt invoice.
 * @param {object} source
 * @returns {{
 *   days: number,
 *   subTotal: number,
 *   packaging: number,
 *   tax: number,
 *   delivery: number,
 *   grandTotal: number,
 *   originalGrandTotal: number | null,
 *   specialDiscountAmount: number,
 *   perDayPrice: number,
 *   gstPercent: number,
 *   distanceKm: number | null,
 *   isFreeDeliveryPlan: boolean,
 *   actualSubtotal: number | null,
 *   discountAmount: number | null,
 *   discountPercent: number,
 * }}
 */
export function resolveSubscriptionChargeTotals(source = {}) {
  const snap = source.webPlanSnapshot && typeof source.webPlanSnapshot === 'object'
    ? source.webPlanSnapshot
    : source;
  const cost = source.subscriptionCost && typeof source.subscriptionCost === 'object'
    ? source.subscriptionCost
    : {};
  const charges = (snap.charges && typeof snap.charges === 'object'
    ? snap.charges
    : (source.charges && typeof source.charges === 'object' ? source.charges : {}));
  const pricing = (snap.pricing && typeof snap.pricing === 'object'
    ? snap.pricing
    : (source.pricing && typeof source.pricing === 'object' ? source.pricing : {}));

  const days = Math.max(
    1,
    Number(
      charges.days
      || snap.deliveryDayCount
      || snap.count
      || source.deliveryDayCount
      || source.originalCount
      || source.count
    ) || 1,
  );

  const subTotal = pickNumber(
    charges.subtotal,
    cost.subTotal,
    snap.subtotal,
    source.subtotal,
    source.estimatedTotal,
  ) ?? 0;
  const packaging = pickNumber(
    charges.totalPackagingCharge,
    cost.totalPackagingCharge,
    snap.totalPackagingCharge,
    source.packagingCharges,
    source.totalPackagingCharge,
  ) ?? 0;
  const tax = pickNumber(
    charges.totalTax,
    cost.totalTax,
    snap.totalTax,
    source.gstAmount,
    source.totalTax,
  ) ?? 0;
  const delivery = pickNumber(
    charges.totalDeliveryCharge,
    cost.totalDeliveryCharge,
    snap.totalDeliveryCharge,
    source.deliveryCharges,
    source.totalDeliveryCharge,
  ) ?? 0;

  const quotedGrandTotal = pickNumber(
    charges.originalTotalPrice,
    charges.totalPrice,
    cost.totalAmount,
    snap.totalPrice,
    source.grandTotal,
    source.estimatedTotal,
  );

  // Live admin-approved / payable amount on persisted subscriptions.
  // Prefer over create-time snapshot so Special Discount receipts stay correct.
  const approvedGrandTotal = isPersistedSubscriptionSource(source)
    ? pickNumber(source.totalPrice)
    : null;

  const grandTotal = approvedGrandTotal != null
    ? approvedGrandTotal
    : (pickNumber(quotedGrandTotal, source.totalPrice, source.grandTotal) ?? 0);

  const originalGrandTotal = quotedGrandTotal != null
    ? quotedGrandTotal
    : (approvedGrandTotal != null ? approvedGrandTotal : null);

  let specialDiscountAmount = pickNumber(
    charges.specialDiscountAmount,
    source.specialDiscountAmount,
  );
  if (specialDiscountAmount == null && originalGrandTotal != null) {
    specialDiscountAmount = roundMoney(Math.max(0, originalGrandTotal - grandTotal));
  }
  specialDiscountAmount = roundMoney(specialDiscountAmount ?? 0);

  const usedApprovedTotal = approvedGrandTotal != null
    && (quotedGrandTotal == null || Math.abs(approvedGrandTotal - quotedGrandTotal) > 0.009);

  const perDayPrice = usedApprovedTotal
    ? roundMoney(grandTotal / days)
    : (pickNumber(charges.perDayPrice, snap.perDayPrice, source.perDayPrice)
      ?? roundMoney(grandTotal / days));

  const distanceRaw = pickNumber(charges.distanceKm, snap.distanceKm, source.distanceKm);
  const actualSubtotal = pickNumber(pricing.actualSubtotal);
  let discountAmount = pickNumber(pricing.discountAmount);
  if (discountAmount == null && actualSubtotal != null) {
    discountAmount = roundMoney(Math.max(0, actualSubtotal - subTotal));
  }
  const discountPercent = Number(pricing.discountPercent) || 0;

  return {
    days,
    subTotal,
    packaging,
    tax,
    delivery,
    grandTotal,
    originalGrandTotal,
    specialDiscountAmount,
    perDayPrice,
    gstPercent: Number(charges.gstPercent ?? snap.gstPercent ?? source.gstPercent) || 5,
    distanceKm: distanceRaw,
    isFreeDeliveryPlan: Boolean(
      charges.isFreeDeliveryPlan ?? snap.isFreeDeliveryPlan ?? source.isFreeDeliveryPlan,
    ),
    actualSubtotal,
    discountAmount,
    discountPercent,
  };
}

function deliveryLabel(totals) {
  if (totals.isFreeDeliveryPlan) return 'Delivery (free with plan)';
  if (totals.distanceKm != null) return `Delivery (${Number(totals.distanceKm).toFixed(1)} km)`;
  return 'Delivery';
}

/**
 * Canonical charge rows shared by Summary, Receipt, Details, and emails.
 * Optional Actual/Discount only when pricing breakdown exists.
 *
 * @param {object} source
 * @param {(n: number) => string} formatMoney
 * @returns {Array<[string, string]>}
 */
export function buildSubscriptionChargeRows(source, formatMoney) {
  const t = resolveSubscriptionChargeTotals(source);
  const rows = [];

  if (t.actualSubtotal != null) {
    rows.push(['Actual price', formatMoney(t.actualSubtotal)]);
    const discLabel = t.discountPercent > 0
      ? `Discount (${t.discountPercent}%)`
      : 'Discount';
    rows.push([discLabel, formatMoney(t.discountAmount ?? 0)]);
  }

  rows.push(
    ['Plan subtotal', formatMoney(t.subTotal)],
    ['Packaging', formatMoney(t.packaging)],
    [`GST (${t.gstPercent}%)`, formatMoney(t.tax)],
    [deliveryLabel(t), formatMoney(t.delivery)],
  );

  if (t.specialDiscountAmount > 0.009) {
    if (t.originalGrandTotal != null) {
      rows.push(['Original total', formatMoney(t.originalGrandTotal)]);
    }
    rows.push(['Special discount', formatMoney(t.specialDiscountAmount)]);
  }

  rows.push(
    ['Total amount', formatMoney(t.grandTotal)],
    ['Per day', formatMoney(t.perDayPrice)],
  );

  return rows;
}
